import React from 'react';
import moment from 'moment';
import { Tooltip, Button } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Linkify from 'react-linkify';
import DOMPurify from 'dompurify';
import BaseMUIDataTable from './BaseMUIDataTable/BaseMUIDataTable';
import { formatDate, formatTime } from '../helpers/formatters';
import { getMuiTableDataIndex } from '../helpers/getters';
import {
  donorLinkFilters,
  getRescuePickupLocationFullName,
  isHTML,
  isRescueClaimed,
  showCloseRescueButtons,
  showICouldnotMakeIt,
} from '../helpers/RescuesHelper';
import DonorLinkPickupIndicator from './DonorLinkPickupIndicator';
import useHasActiveFeatureFlag from '../hooks/useFeatureFlags';
import { FF_DONOR_LINK } from './FeatureFlagWrapper';
import { sortByDateAndPickupWindowStartAndEndTime, sortByPickupReady } from '../helpers/sorters';
import RescuesTableStatusBox, { useRescuesTableRowColorStyles } from './Common/RescuesTableStatusBox';

const customActionsButtonsSx = {
  marginRight: '6px',
  fontSize: '11px',
  padding: '6px',
};

const RescuerRecentRescuesTable = ({
  tableId,
  rescues,
  isLoading,
  onCloseRescueButtonClick,
  onCloseNoShowRescueButtonClick,
  onCellClick,
  user,
}) => {
  const tableStyles = useRescuesTableRowColorStyles();

  const hasActiveDonorLinkFF = useHasActiveFeatureFlag(FF_DONOR_LINK);

  const pickupReadyCustomBodyRender = (value, tableMeta, csv = false) => {
    const rescue = rescues[getMuiTableDataIndex(tableMeta)];

    if (!rescue) {
      return null;
    }

    if (!rescue?.location_donor_link_active) {
      return '-';
    }

    if (csv) {
      return value ? 'Pickup Ready' : 'No food yet';
    }

    return <DonorLinkPickupIndicator pickupReady={!!value} />;
  };

  const foodDonorCustomBodyRender = (value, tableMeta) => {
    const rescue = rescues[getMuiTableDataIndex(tableMeta)];

    if (!rescue) {
      return null;
    }

    return getRescuePickupLocationFullName(rescue);
  };

  const dateCustomBodyRender = (value, tableMeta, csv = false) => {
    const formattedDate = moment(value, 'YYYYMMDD').format('MM/DD/YYYY');

    if (csv) {
      return formattedDate;
    }

    const rescue = rescues[getMuiTableDataIndex(tableMeta)];

    return (
      <>
        <span>{formattedDate}</span>

        <RescuesTableStatusBox rescue={rescue} />
      </>
    );
  };

  return (
    <ThemeProvider
      theme={(theme) =>
        createTheme({
          ...theme,
          components: {
            ...theme.components,
            MUIDataTableHeadCell: {
              styleOverrides: {
                root: {
                  ...theme.components.MUIDataTableHeadCell.styleOverrides.root,
                  '&:last-child': {
                    width: 255,
                  },
                },
              }
            },
          },
        })
      }
    >
      <BaseMUIDataTable
        tableId={tableId}
        isLoading={isLoading}
        data={rescues}
        columns={[
          {
            name: 'id',
            label: 'ID',
            options: {
              filter: false,
              sort: false,
              display: false,
            },
          },
          {
            name: 'date',
            label: 'Date',
            options: {
              filterOptions: {
                renderValue: formatDate,
              },
              sortCompare:
                (order) =>
                ({ rowData: row1 }, { rowData: row2 }) =>
                  sortByDateAndPickupWindowStartAndEndTime({
                    order,
                    firstDate: row1[1],
                    firstBegin: row1[3],
                    firstEnd: row1[4],
                    secondDate: row2[1],
                    secondBegin: row2[3],
                    secondEnd: row2[4],
                  }),
              setCellProps: () => ({ sx: { width: 125 } }),
              customBodyRender: (value, tableMeta) => dateCustomBodyRender(value, tableMeta),
              customBodyRenderCSV: (value, tableMeta) => dateCustomBodyRender(value, tableMeta, true),
            },
          },
          {
            name: 'day',
            label: 'Day',
            options: {
              setCellProps: () => ({ sx: { width: 125 } }),
              customBodyRenderCSV: (value, tableMeta) =>
                moment(rescues[getMuiTableDataIndex(tableMeta)].date, 'YYYYMMDD').format('dddd'),
              customBodyRender: (value, tableMeta) =>
                moment(rescues[getMuiTableDataIndex(tableMeta)].date, 'YYYYMMDD').format('dddd'),
            },
          },
          {
            name: 'pickup_begin',
            label: 'Pickup Begin',
            options: {
              filter: false,
              setCellProps: () => ({ sx: { width: 125 } }),
              customBodyRenderCSV: formatTime,
              customBodyRender: value => formatTime(value),
            },
          },
          {
            name: 'pickup_end',
            label: 'Pickup End',
            options: {
              filter: false,
              setCellProps: () => ({ sx: { width: 125 } }),
              customBodyRenderCSV: formatTime,
              customBodyRender: value => formatTime(value),
            },
          },
          {
            name: 'location',
            label: 'Food Donor',
            options: {
              customFilterListOptions: {
                render: value => `Food Donor: ${value}`,
              },
              customBodyRenderCSV: foodDonorCustomBodyRender,
              customBodyRender: foodDonorCustomBodyRender,
            },
          },
          {
            name: 'receiver',
            label: 'Receiving Agency',
            options: {
              customBodyRender: value => value || '',
            },
          },
          ...(hasActiveDonorLinkFF
            ? [
                {
                  name: 'rescue_confirmed',
                  label: 'Donor Link',
                  options: {
                    display: true,
                    filter: true,
                    customBodyRender: (value, tableMeta) => pickupReadyCustomBodyRender(value, tableMeta),
                    customBodyRenderCSV: (value, tableMeta) => pickupReadyCustomBodyRender(value, tableMeta, true),
                    filterOptions: {
                      names: [
                        donorLinkFilters.dataReceived,
                        donorLinkFilters.dataNotReceived,
                        donorLinkFilters.donorLinkInactive,
                        donorLinkFilters.donorLinkActive,
                      ],
                      logic: (option, filters, row) => {
                        if (!filters || filters.length === 0) {
                          return false;
                        }

                        const rescueId = row[0];
                        const rescue = rescues.find((r) => r.id === rescueId);

                        switch (filters[0]) {
                          case donorLinkFilters.dataReceived:
                            return !rescue.rescue_confirmed;
                          case donorLinkFilters.dataNotReceived:
                            return !!option;
                          case donorLinkFilters.donorLinkInactive:
                            return rescue.location_donor_link_active;
                          case donorLinkFilters.donorLinkActive:
                            return !rescue.location_donor_link_active;
                          default:
                            return true;
                        }
                      },
                    },
                    customFilterListOptions: {
                      render: (value) => `Donor Link: ${value}`,
                    },
                    sortCompare: (order) => (row1, row2) => {
                      const rescue1 = rescues.find((fd) => fd.id === row1.rowData[0]);
                      const rescue2 = rescues.find((fd) => fd.id === row2.rowData[0]);

                      return sortByPickupReady(order, rescue1, rescue2);
                    },
                  },
                },
              ]
            : []),
          {
            name: 'slug',
            label: 'Description',
            options: {
              filter: false,
            },
          },
          {
            name: 'pickup_notes',
            label: 'Pickup Notes',
            options: {
              customBodyRender: (value) => {
                if (!value) {
                  return '-';
                }

                if (isHTML(value)) {
                  return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }} />
                }

                return (
                  <Linkify
                    componentDecorator={(decoratedHref, decoratedText, key) => (
                      <a target="blank" href={decoratedHref} key={key}>
                        {decoratedText}
                      </a>
                    )}
                  >
                    {value}
                  </Linkify>
                );
              },
              filter: false,
            },
          },
          {
            name: 'actions',
            label: 'Actions',
            options: {
              download: false,
              Empty: true,
              filter: false,
              searchable: false,
              viewColumns: false,
              customBodyRender: (value, tableMeta) => {
                const rescue = rescues[getMuiTableDataIndex(tableMeta)];

                if (!rescue || rescue.closed_by_id !== null) {
                  return false;
                }

                return (
                  <>
                    {showCloseRescueButtons(rescue, user) && (
                      <Tooltip
                        placement="top"
                        key="Yes I did it!"
                        title="Close rescue"
                        sx={customActionsButtonsSx}
                      >
                        <Button
                          type="button"
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => onCloseRescueButtonClick(rescue)}
                        >
                          Yes I did it!
                        </Button>
                      </Tooltip>
                    )}

                    {showICouldnotMakeIt(rescue, user) && (
                      <Tooltip
                        placement="top"
                        key="I couldn't make it"
                        title="Close rescue"
                        sx={customActionsButtonsSx}
                      >
                        <Button
                          type="button"
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => onCloseNoShowRescueButtonClick(rescue)}
                        >
                          I couldn't make it
                        </Button>
                      </Tooltip>
                    )}
                  </>
                );
              },
            },
          },
        ]}
        options={{
          elevation: 0,
          rowsPerPage: 15,
          selectableRows: 'none',
          responsive: 'simple',
          onCellClick: (colData, cellMeta) => {
            // ignore click on "actions" column cell
            if (cellMeta.colIndex >= 9) {
              return false;
            }

            const rescue = rescues[getMuiTableDataIndex(cellMeta)];

            if (!rescue) {
              return false;
            }

            return onCellClick(rescue);
          },
          setRowProps: (row, dataIndex) => {
            const rescue = rescues[dataIndex];

            let colorClass = tableStyles.claimedRescueRow;

            if (rescue.cancelled_by_id) {
              colorClass = tableStyles.cancelledRescueRow;
            } else if (rescue.closed_by_id || rescue.completed !== null) {
              colorClass = tableStyles.closedRescueRow;
            } else if (isRescueClaimed(rescue)) {
              colorClass = tableStyles.claimedRescueRow;
            } else if (!isRescueClaimed(rescue)) {
              colorClass = tableStyles.unclaimedRescueRow;
            }

            return {
              className: colorClass,
            };
          },
        }}
      />
    </ThemeProvider>
  );
};

export default RescuerRecentRescuesTable;
